import { toast } from "react-toastify";
import { i18n } from "../translate/i18n";
import { isString } from 'lodash';
import { genericFallbackMessage, isTransientNetworkIssue, sanitizeToastMessage } from "../utils/toastPolicy";
import { isRequestCanceled } from "../utils/requestCancel";
import { LICENSE_ERROR_CODE } from "../helpers/licenseNotice";

const toastError = err => {
    // Requisição cancelada de propósito (troca de ticket/aba): nada a avisar.
    if (isRequestCanceled(err)) return;

    // Log detalhado do erro para debug
    console.error("[toastError] Erro recebido:", err);
    console.error("[toastError] err.response:", err.response);
    console.error("[toastError] err.response?.data:", err.response?.data);

    // Durante restart/redeploy o backend pode ficar indisponivel por poucos segundos.
    // Nesses casos, loga para suporte e evita popup tecnico para o usuario.
    if (isTransientNetworkIssue(err)) {
        console.warn("[toastError] Toast suprimido por indisponibilidade temporaria:", err);
        return;
    }
    
    const errorMsg = err.response?.data?.error;
    const errorStatus = err.response?.status;

    // Empresa bloqueada por vencimento (402): o interceptor do api.js já
    // redireciona para /financeiro; lá o card de assinatura explica a situação.
    // Evita spam de toast das várias chamadas do layout que falham juntas.
    if (errorStatus === 402 || errorMsg === "ERR_COMPANY_BLOCKED") {
        if (window.location.pathname === "/financeiro") {
            return;
        }
        toast.error(i18n.t("backendErrors.ERR_COMPANY_BLOCKED"), {
            toastId: "company_blocked",
            autoClose: 5000,
            theme: "light",
        });
        return;
    }
    
    // Licença Empsis inválida: quem explica é o aviso fixo da tela de login
    // (services/api.js e useAuth gravam o motivo). Evita uma enxurrada de
    // toasts das várias chamadas que falham juntas antes do redirecionamento.
    if (errorMsg === LICENSE_ERROR_CODE) {
        return;
    }

    // Cloud API: janela de 24h expirada - NUNCA exibir como toast
    // Este erro deve ser tratado pelo componente que fez a requisição (abrir template picker)
    if (errorMsg === "ERR_CLOUDAPI_WINDOW_CLOSED") {
        console.log("[toastError] Suppressed ERR_CLOUDAPI_WINDOW_CLOSED toast - should be handled by component");
        return;
    }
    
    // Tratamento especial para erros de permissão (403)
    if (errorStatus === 403 && errorMsg === "ERR_NO_PERMISSION") {
        toast.error(i18n.t(`backendErrors.${errorMsg}`), {
            toastId: errorMsg,
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",
        });
        return;
    }
    
    // Tratamento especial para erros de autenticação (401)
    if (errorStatus === 401) {
        // Verifica se é erro de credenciais inválidas (login)
        const isInvalidCredentials = errorMsg && errorMsg.includes("ERR_INVALID_CREDENTIALS");
        
        if (isInvalidCredentials) {
            // Credenciais inválidas - exibe mensagem de erro de login
            toast.error(i18n.t("backendErrors.ERR_INVALID_CREDENTIALS"), {
                toastId: "invalid_credentials",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: "light",
            });
            return;
        }
        
        // Verifica se havia indicadores de sessão ativa
        const hadToken = !!localStorage.getItem("token");
        const hadCompanyId = !!localStorage.getItem("companyId");
        const hadUserId = !!localStorage.getItem("userId");
        const wasLoggedIn = hadToken && hadCompanyId && hadUserId;
        
        // Só mostra toast de sessão expirada se o usuário estava realmente logado
        if (wasLoggedIn) {
            toast.error(i18n.t("login.sessionExpired"), {
                toastId: "session_expired",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: "light",
            });
        }
        // Se não estava logado, não mostra nada - é esperado receber 401
        return;
    }
    
    if (errorMsg) {
        const errorDetails = err.response?.data?.details;
        const backendMessage = err.response?.data?.message;
        
        // Lista de erros cujos detalhes NÃO devem ser exibidos ao usuário
        // (são para uso programático no código)
        const errorsToHideDetails = [
            'ERR_OTHER_OPEN_TICKET',
            'ERR_TICKETS_FROM_OTHER_USERS'
        ];
        
        const shouldHideDetails = errorsToHideDetails.includes(errorMsg);
        const displayMsg = (!shouldHideDetails && errorDetails)
            ? `${backendMessage || errorMsg}\n${errorDetails}`
            : (backendMessage || errorMsg);

        if (i18n.exists(`backendErrors.${errorMsg}`)) {
            const translatedMsg = i18n.t(`backendErrors.${errorMsg}`);
            const fullMsg = (!shouldHideDetails && errorDetails) ? `${translatedMsg}\n${errorDetails}` : translatedMsg;
            toast.error(fullMsg, {
                toastId: errorMsg,
                autoClose: 4000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: "light",
                style: { whiteSpace: "pre-line" },
            });
            return;
        } else {
            toast.error(sanitizeToastMessage(displayMsg) || genericFallbackMessage(), {
                toastId: errorMsg,
                autoClose: 4000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: "light",
                style: { whiteSpace: "pre-line" },
            });
            return;
        }
    }

    if (err && typeof err === "object" && isString(err.message) && err.message.trim()) {
        toast.error(sanitizeToastMessage(err.message) || genericFallbackMessage(), {
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",
        });
        return;
    }

    if (isString(err)) {
        toast.error(sanitizeToastMessage(err) || genericFallbackMessage(), {
            autoClose: 2000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: false,
            draggable: true,
            progress: undefined,
            theme: "light",
        });
        return;
    } else {
        // Log para debug se cair no erro genérico
        console.error("[toastError] Erro genérico - estrutura não reconhecida");
        console.error("[toastError] Tipo do erro:", typeof err);
        console.error("[toastError] Keys do erro:", Object.keys(err || {}));
        
        toast.error(genericFallbackMessage(), {
            autoClose: 2000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: false,
            draggable: true,
            progress: undefined,
            theme: "light",
        });
        return;
    }
};

export default toastError;
